import { z } from 'zod'
import { MonocleJobSummaryDto } from './monocleJob'
import { vector3 } from './utils'
import { MonocleVideoPlaybackDto } from './monocleVideo'

export const Display3DModelAssetMetadataDto = z.object({
  md5: z.string().optional(),
  size: z.coerce.number().optional(),
})

export const Display3DModelOptionsDto = z.object({
  /**
   * Camera euler rotation angles (degrees)
   * Pitch (the x component) is the rotation about the x-axis.
   * Yaw (the y component) is the rotation about the y-axis.
   * Roll (the z component) is the rotation about the z-axis.
   * (default: [0, 0, 0])
   */
  camera_euler_angles: vector3.default([0, 0, 0]),

  /**
   * Camera pan (meters)
   * [x, y, z]
   * (default: [0, 0, 0])
   */
  camera_pan: vector3.default([0, 0, 0]),

  wall_mounted: z.boolean().default(false),
})

export const DisplayInfoDto = z.object({
  title: z.string().default(''),
  email: z.string().default(''),
  description: z.string().default(''),
  location_name: z.string().default(''),
})

// Be permissive: if the datetime is already a Date, normalize it to ISO.
const IsoDateTime = z
  .string()
  .datetime()
  .or(z.date().transform((value) => value.toISOString()))

export const Display3DModelDto = z.object({
  video_url: z.string().optional(),
  video: MonocleVideoPlaybackDto.optional(),
  thread_id: z.string().optional(),
  jobs: z.array(MonocleJobSummaryDto).optional(),
  created: IsoDateTime,
  modified: IsoDateTime,

  /**
   * USDZ model file URL of bundle
   * - bundle api: full resolved storage url for source bundle
   * - at-rest: base filename, ex: `<bundle_id>.usdz`
   * - distribution api: fully resolved storage API url, ex: `https://firebasestorage.googleapis.com/v0/b/monocle-spdj-production.appspot.com/o/users%2F<user_id>%2Fbundles%2F<bundle_id>%2F<model_bundle_id>.usdz?alt=media&token=<token_id>`
   */
  usdz_url: z.string(),

  /**
   * GLB model file URL of bundle
   * - bundle api: full resolved storage url for source bundle
   * - at-rest: base filename, ex: `<bundle_id>.glb`
   * - distribution api: fully resolved storage API url, ex: `https://firebasestorage.googleapis.com/v0/b/monocle-spdj-production.appspot.com/o/users%2F<user_id>%2Fbundles%2F<bundle_id>%2F<model_bundle_id>.glb?alt=media&token=<token_id>`
   */
  glb_url: z.string(),

  /**
   * Storage metadata for the active USDZ/GLB model assets. The GLB MD5 is the
   * preferred signal for whether model-viewer needs a full reload.
   */
  usdz_metadata: Display3DModelAssetMetadataDto.optional(),
  glb_metadata: Display3DModelAssetMetadataDto.optional(),

  /**
   * Poster thumbnail of bundle
   *
   * Format:
   * - JPG encoding
   * - limited to 64KB
   * - resolution under 1024x768
   *   (@2x retina 512x384)
   * - aspect ratio 4:3
   * - Generally size is 640x480
   *
   * Base64 encoded data URL
   */
  poster_jpg_data_url: z.string(),

  /**
   * Poster thumbnail file URL of bundle
   * - bundle api: full resolved bundle-api url, ex: `/api/bundle/<bundle_id>/poster.jpg`
   * - at-rest: base filename, ex: `<bundle_id>_poster.jpg`
   * - distribution api: fully resolved storage API url, ex: `https://firebasestorage.googleapis.com/v0/b/monocle-spdj-production.appspot.com/o/users%2F<user_id>%2Fbundles%2F<bundle_id>%2Fposter.jpg?alt=media&token=<token_id>`
   */
  poster_jpg_url: z.string(),

  /**
   * Poster thumbnail mask of bundle
   *
   * Format:
   * - PNG encoding
   * - Limited to 64KB
   * - Matching resolution to poster_jpg_url
   *
   * Base64 encoded data URL
   */
  poster_mask_png_data_url: z.string(),

  /**
   * Poster thumbnail mask file URL of bundle
   * - bundle api: full resolved bundle-api url, ex: `/api/bundle/<bundle_id>/poster_mask.png`
   * - at-rest: base filename, ex: `<bundle_id>_poster_mask.png`
   * - distribution api: fully resolved storage API url, ex: `https://firebasestorage.googleapis.com/v0/b/monocle-spdj-production.appspot.com/o/users%2F<user_id>%2Fbundles%2F<bundle_id>%2Fposter_mask.png?alt=media&token=<token_id>`
   */
  poster_mask_png_url: z.string(),

  /**
   * Min bound of vertices in scan (meters)
   * [x, y, z](readonly)
   */
  min_coordinate: vector3,

  /**
   * Max bound of vertices in scan (meters)
   * [x, y, z](readonly)
   */
  max_coordinate: vector3,

  info: DisplayInfoDto,
  options: Display3DModelOptionsDto,
})

export const DistributionManifestBundleEntryDto = z.object({
  bundle_id: z.string(),
  model: Display3DModelDto,
})

export const DistributionManifestDto = z.object({
  version: z.string(),
  owner: z.string(),
  packaged_bundle_id: z.string(),
  collection_id: z.string(),
  distribution_key_id: z.string(),
  created: IsoDateTime,
  bundles: z.array(DistributionManifestBundleEntryDto),
})

export const DistributionKeyDto = DistributionManifestDto.extend({
  etag: z.string(),
  key: z.object({
    id: z.string(),
    active_id: z.string().nullable(),
    modified: IsoDateTime,
  }),
})

export type Display3DModelOptions = z.infer<typeof Display3DModelOptionsDto>
export type DisplayInfo = z.infer<typeof DisplayInfoDto>
export type Display3DModel = z.infer<typeof Display3DModelDto>
export type DistributionManifestBundleEntry = z.infer<
  typeof DistributionManifestBundleEntryDto
>
export type DistributionManifest = z.infer<typeof DistributionManifestDto>
export type DistributionKey = z.infer<typeof DistributionKeyDto>
